import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Gift } from "lucide-react";

export default function Register() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const { t } = useI18n();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [username, setUsername] = useState("");
    const [referralCode, setReferralCode] = useState(() => {
        try { return localStorage.getItem("p2w_ref") || ""; } catch { return ""; }
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const ref = searchParams.get("ref");
        if (ref) {
            const code = ref.toUpperCase();
            setReferralCode(code);
            try { localStorage.setItem("p2w_ref", code); } catch { /* noop */ }
        }
    }, [searchParams]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await register(email, password, username, referralCode || undefined);
            try { localStorage.removeItem("p2w_ref"); } catch { /* noop */ }
            toast.success("¡Cuenta creada! Revisa tu email para verificarlo.");
            navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo crear la cuenta");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="register-page">
            <div className="w-full max-w-md relative">
                <div className="glow-orb bg-p2w-purple w-[300px] h-[300px] -top-20 -left-20 opacity-30" />
                <div className="relative rounded-2xl border border-p2w-line bg-p2w-surface p-8 shadow-glow-violet">
                    <div className="flex items-center gap-2 mb-1 text-p2w-green">
                        <Gift className="w-4 h-4" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-bold font-sora">
                            +1.000 pts bienvenida
                        </span>
                    </div>
                    <h1 className="font-sora font-extrabold text-3xl tracking-tight">{t("auth.register_title")}</h1>
                    <p className="mt-1 text-p2w-gray text-sm">{t("auth.register_sub")}</p>

                    <form onSubmit={onSubmit} className="mt-6 space-y-4">
                        <div>
                            <Label className="text-p2w-gray text-xs uppercase tracking-widest">{t("auth.username")}</Label>
                            <Input
                                data-testid="register-username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                minLength={2}
                                maxLength={24}
                                required
                                className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                            />
                        </div>
                        <div>
                            <Label className="text-p2w-gray text-xs uppercase tracking-widest">{t("auth.email")}</Label>
                            <Input
                                type="email"
                                data-testid="register-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                            />
                        </div>
                        <div>
                            <Label className="text-p2w-gray text-xs uppercase tracking-widest">{t("auth.password")}</Label>
                            <Input
                                type="password"
                                data-testid="register-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                minLength={6}
                                required
                                className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                            />
                        </div>
                        <div>
                            <Label className="text-p2w-gray text-xs uppercase tracking-widest">
                                Código de referido <span className="text-p2w-gray normal-case">(opcional)</span>
                            </Label>
                            <Input
                                data-testid="register-referral"
                                value={referralCode}
                                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                                placeholder="Ej. AB12CD"
                                maxLength={6}
                                className="mt-2 bg-p2w-elevated border-p2w-line text-white font-mono uppercase focus-visible:ring-p2w-green/40"
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid="register-submit"
                            disabled={submitting}
                            className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 shadow-glow-green font-semibold py-6 rounded-xl"
                        >
                            {submitting ? "Creando…" : t("auth.register_cta")}
                        </Button>
                    </form>

                    <p className="mt-6 text-sm text-p2w-gray text-center">
                        {t("auth.have_account")}{" "}
                        <Link to="/login" className="text-p2w-green hover:underline font-semibold">
                            {t("nav.login")}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
